import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store";
import { SortDescriptor } from "@progress/kendo-data-query";
import { GridColumn } from "@progress/kendo-react-grid";
import {
  AdminContentWrapper,
  AdminHeader,
  Loader,
  TableGrid,
  TableLastItem,
} from "../../components";
import { format } from "date-fns";
import {
  clearAdminEigentuemerwechselData,
  getAdminEigentuemerwechsel,
} from "../../store/slices/eigentuemerwechsel";
import appConfig from "../../util/appConfig";
import { statuses } from "../AdminEigentuemerwechselById";
import API from "../../api/api";

const AdminEigentuemerwechsel = () => {
  const dispatch = useAppDispatch();
  const { isAdminLoaderActive } = useAppSelector((state) => state.admin);
  const { data, itemsCount } = useAppSelector(
    (state) => state.eigentuemerwechsel.admin,
  );
  const [sort, setSort] = useState<Array<SortDescriptor>>([]);
  const [value, setValue] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [datesChanged, setDatesChanged] = useState({});
  const [status, setStatus] = useState<string[]>([]);

  const downloadExcel = async () => {
    if (["mags"].includes(appConfig.logo)) {
      await API.eigentuemerwechsel.get.downloadExcel({
        startDate: startDate ? new Date(startDate).toUTCString() : startDate,
        endDate: endDate ? new Date(endDate).toUTCString() : endDate,
        searchValue: value,
        status,
        direction: sort[0]?.dir,
        sort: sort[0]?.field,
      });
    }
  };

  const onDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    if (end || end === null) {
      setDatesChanged({ updated: true });
    }
  };

  const changeStatuses = (st: string[]) => {
    setStatus([...st]);
  };

  const changeHandler = (val: string) => {
    setValue(val);
  };

  const pageChangeHandler = (page: number) => {
    setPage(page);
  };

  const pageSizeHandler = (size: number) => {
    setPage(1);
    setPageSize(size);
  };

  const generateStatusOptions = (type?: string) => {
    switch (type) {
      case "mags": {
        return [
          { label: statuses.check, value: "check" },
          { label: statuses.accepted, value: "accepted" },
          { label: statuses.declined, value: "declined" },
          { label: statuses.inWork, value: "inWork" },
        ];
      }
      case "viersen": {
        return [
          { label: statuses.check, value: "check" },
          { label: statuses.accepted, value: "accepted" },
          { label: statuses.declined, value: "declined" },
        ];
      }
      default: {
        return [
          { label: statuses.check, value: "check" },
          { label: statuses.accepted, value: "accepted" },
          { label: statuses.declined, value: "declined" },
          { label: statuses.inWork, value: "inWork" },
        ];
      }
    }
  };

  const selectorOptions = [
    {
      placeholder: "Bitte Status wählen",
      isActive: ["mags", "viersen"].includes(appConfig.logo),
      defaultValue: undefined,
      selectOptions: generateStatusOptions(appConfig.logo),
      value: status.map((el) => {
        return {
          value: el,
          label: statuses[el],
        };
      }),
      onChange: changeStatuses,
    },
  ];

  useEffect(() => {
    const abortController = new window.AbortController();
    const signal = abortController.signal;

    dispatch(
      getAdminEigentuemerwechsel({
        signal,
        direction: sort[0]?.dir,
        page,
        pageSize,
        searchValue: value,
        sort: sort[0]?.field,
        startDate: startDate ? new Date(startDate).toUTCString() : startDate,
        endDate: endDate ? new Date(endDate).toUTCString() : endDate,
        status,
      }),
    );

    return () => {
      if (signal && abortController.abort) {
        abortController.abort();
      }
    };
  }, [sort, page, pageSize, value, datesChanged, status]);

  useEffect(() => {
    return () => {
      dispatch(clearAdminEigentuemerwechselData());
    };
  }, []);

  const generateColumns = (env?: string) => {
    switch (env) {
      case "viersen": {
        return [
          { field: "status", title: "Status" },
          { field: "createdAt", title: "Datum (Meldung)" },
          { field: "kassenzeichen", title: "Kassenzeichen" },
          { field: "ort", title: "Ort" },
          { field: "street", title: "Straße" },
          { field: "previousOwnerFullName", title: "Vorheriger Besitzer" },
          { field: "newOwnerFullName", title: "Neuer Besitzer" },
        ];
      }
      default: {
        return [
          { field: "createdAt", title: "Datum" },
          { field: "ort", title: "Ort" },
          { field: "street", title: "Straße" },
          { field: "previousOwnerFullName", title: "Vorheriger Besitzer" },
          { field: "newOwnerFullName", title: "Neuer Besitzer" },
          ...(["mags"].includes(appConfig.logo)
            ? [{ field: "status", title: "Status" }]
            : []),
        ];
      }
    }
  };

  return (
    <div>
      <AdminHeader pageTitle={"Eigentümerwechsel"} hasBtnGroup={false} />
      <AdminContentWrapper>
        <TableGrid
          data={data.map((el) => {
            return {
              ...el,
              // name: `${el.username} ${
              //   el.userSurname ? ", " + el.userSurname : ""
              // }`,
              // status: statuses[el.status],
              previousOwnerFullName: `${el.previousOwnerSurname}, ${el?.previousOwnerName ?? ""}`,
              newOwnerFullName: ` ${el.newOwnerSurname}, ${el?.newOwnerName ?? ""}`,
              createdAt: format(new Date(el.createdAt), "dd.MM.yyyy HH:mm"),
              status: el?.status ? statuses[el.status] : "",
              street: `${el?.strasse} ${el?.hausnr}${
                el?.hausnrzusatz ? el.hausnrzusatz : ""
              }${el?.hausnrto ? ` - ${el?.hausnrto}` : ""}`.trim(),
            };
          })}
          sort={sort}
          sortable={true}
          onSortChange={setSort}
          withExcelExport={true}
          items={generateColumns(appConfig.logo)}
          searchInputValue={value}
          onSearchInputValueChange={changeHandler}
          withPager={true}
          page={page}
          pageSize={pageSize}
          pageChangeHandler={pageChangeHandler}
          pageSizeHandler={pageSizeHandler}
          itemsCount={itemsCount}
          onDateChange={onDateChange}
          startDate={startDate}
          endDate={endDate}
          withDatePicker
          lastItem={<GridColumn cell={TableLastItem} width="50px" />}
          selectorOptions={selectorOptions.filter((el) => el.isActive)}
          onExcelExportClickHandler={downloadExcel}
        />
      </AdminContentWrapper>
      {isAdminLoaderActive && <Loader />}
    </div>
  );
};

export default AdminEigentuemerwechsel;
